import React, { useState } from "react";
import "../../../../../../css/CashflowModeller2/Cards/Pension/PensionCardSmall.css";
import "../../../../../../css/CashflowModeller2/Cards/Cards.css";
import AddEditButton from "../../Buttons/AddEditButton";
import Input1 from "../../Inputs/Input1";
import InputSmall from "../../Inputs/InputSmall";
import Input2 from "../../Inputs/Input2";
import { useCfmContext } from "../../../Context/CfmContext";
import PensionDialogBox from "../../DialogBoxes/PensionDialogBox";
import TimelineOptions from "../../../Utils/TimelineOptions";
import DrawdownOptions from "../../../Utils/DrawdownOptions";
import ArrowDown from "../../Buttons/ArrowDown";
import { useWindowWidth } from "../../../Hooks/UseWindowWidth";

const firstWord = (s = "") => s.trim().split(/\s+/)[0] ?? "";

const makeResponsiveLabel = (pension) => {
  if (!pension?.name || !pension?.type) return "Name";
  return `${pension.name.split(" ")[0]} - ${pension.type}`;
};

/**
 * PensionCardSmall is a React functional component that displays a summary card for pension information.
 *
 * Features:
 * - Shows a list of pensions from the context state, or a default empty pension if none exist.
 * - Allows toggling the card's collapsed/expanded state.
 * - Provides an Add/Edit button to open a dialog for pension details (disabled if no details exist).
 * - Displays pension fund value, contribution, and growth rate in read-only input fields.
 * - Renders drawdown details for each pension, including value, start, and end, in read-only input fields.
 *
 * State:
 * - isDialogOpen: Controls visibility of the pension dialog box.
 * - isCollapsed: Controls whether the card content is collapsed or expanded.
 *
 * Context:
 * - Uses `useCfmContext` to access pension data and related state.
 *
 * @component
 * @returns {JSX.Element} The rendered PensionCardSmall component.
 */
const PensionCardSmall = () => {
  const { state } = useCfmContext();
  const width = useWindowWidth();
  const pensions =
    state.pensions?.length > 0
      ? state.pensions
      : [
          {
            name: "",
            type: "",
            fundValue: {
              value: "",
              unit: TimelineOptions[1].value,
            },
            atr: "",
            drawdowns: [
              {
                value: "",
                unit: TimelineOptions[1].value,
                start: "",
                end: "",
              },
            ],
          },
        ];

  const isDisabled = state.pensions?.length === 0;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);

  // useEffect(() => {
  //   console.log(state.pensions);
  // }, [state]);
  return (
    <div className={`card-wrapper ${isCollapsed ? "collapsed" : ""}`}>
      <div className="card-rows card-header">
        <div className="Title">Pension</div>
        <div className="card-header-actions">
          <div className="relative-button-wrapper">
            <AddEditButton
              onClick={() => setIsDialogOpen(true)}
              isDisabled={state.details?.length === 0}
            />
            {isDialogOpen && (
              <div className="dialog-overlay">
                <PensionDialogBox onClose={() => setIsDialogOpen(false)} />
              </div>
            )}
          </div>
          <div
            className={`arrow-toggle ${isCollapsed ? "rotate" : ""}`}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <ArrowDown />
          </div>
        </div>
      </div>
      <div
        className={`card-collapse-container ${isCollapsed ? "collapsed" : ""}`}
      >
        {pensions.map((pension, index) => (
          <div key={index} className="card-columns">
            <div className="card-rows">
              <Input1
                // label={
                //   pension?.name && pension?.type
                //     ? `${pension.name.split(" ")[0]} - ${
                //         pension.type.split(" ")[0]
                //       }`.length > 10
                //       ? `${pension.name.split(" ")[0]} - ${
                //           pension.type.split(" ")[0]
                //         }`.slice(0, 12) + ".."
                //       : `${pension.name} - ${pension.type}`
                //     : `Name`
                // }
                label={makeResponsiveLabel(pension, width)}
                value={pension.fundValue?.value}
                type="number"
                currency={true}
                // currency
                readOnly
                isDisabled={isDisabled}
              />
              <InputSmall
                label="Cont."
                value={pension.contribution?.value}
                readOnly
                isDisabled={isDisabled}
                currency={true}
              />
              <InputSmall
                label="Growth"
                value={pension.atr}
                readOnly
                isDisabled={isDisabled}
                per={true}
              />
            </div>
            {pension.drawdowns.length > 0 &&
              (pension.drawdowns || []).map((dd, ddIndex) => (
                <div key={ddIndex}>
                  <div className="no-dovider-dialogBox" />
                  <div className="card-rows" key={ddIndex}>
                    <Input2
                      label={ddIndex === 0 ? "Drawdown" : ""}
                      value={dd.value}
                      onChange={() => {}}
                      isDisabled={isDisabled}
                      options={TimelineOptions}
                      type="number"
                      currency={dd.unit !== DrawdownOptions[0].value}
                      per={dd.unit === DrawdownOptions[0].value}
                      readOnly
                    />
                    <InputSmall
                      label={ddIndex === 0 ? "Start" : ""}
                      value={dd.start}
                      isDisabled={isDisabled}
                      readOnly
                    />
                    <InputSmall
                      label={ddIndex === 0 ? "End" : ""}
                      value={dd.end}
                      isDisabled={isDisabled}
                      readOnly
                    />
                  </div>
                </div>
              ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PensionCardSmall;
